import { Component, inject, OnInit } from '@angular/core';
import { CommonService as LocalCommonService } from 'src/app/services/common.service';
import {
  AgreementItem,
  AgreementItemEssential,
  AssetManagementItem,
  AttachmentsItem,
  DdtItem,
  DdtItemEssential,
  DeliveryItem,
  DeliveryItemEssential,
  OrderCustomerItem,
  OrderCustomerItemEssential,
  OrderItem,
  OrderItemEssential,
  ScheduleItem,
  TicketItem,
  WarehouseStockItem,
} from '../api/models';
import { DialogService } from 'primeng/dynamicdialog';
import {
  AttachmentEntity,
  AttachmentType,
  ModalUploadComponent,
  ModalUploadData,
} from '../shared/modal-upload/modal-upload.component';
import { MenuItem, MessageService } from 'primeng/api';
import { ListComponent } from './list.component';
import { AdvancedListComponent } from './advanced-list.component';
import { AllegatiService } from '../api/services';
import { downloadFile, fileNameFromHeaders } from './utils';

export abstract class BaseUploadComponent extends ListComponent {
  private localCommon = inject(LocalCommonService);
  protected dialogService = inject(DialogService);
  abstract type: AttachmentType;
  protected attachments: AttachmentsItem[] = [];
  protected selectedEntity?: AttachmentEntity;

  protected tbl_attachments: MenuItem[] = [];

  async loadAttachments() {
    this.localCommon.attachments.subscribe({
      next: attachments => {
        this.attachments = attachments.filter(a => {
          switch (this.type) {
            case 'agreement':
              return a.for_agreement;
            case 'asset_management':
              return a.for_asset_management;
            case 'ddt':
              return a.for_ddt;
            case 'delivery':
              return a.for_delivery;
            case 'order':
              return a.for_order;
            case 'order_customer':
              return a.for_order_customer;
            case 'schedule':
              return a.for_schedule;
            case 'ticket':
              return a.for_ticket;
            case 'warehouse':
              return a.for_warehouse;
            default:
              return false;
          }
        });
      },
    });
  }

  protected openUploadActions(
    event: Event,
    entity: AttachmentEntity,
    header: string = `Allegati per ${this.entityType}`
  ) {
    event.stopPropagation();

    this.openUploadModal(
      {
        selectedEntity: entity,
        entityType: this.type,
      },
      header
    );
  }

  protected openUploadModal({ selectedEntity, entityType }: ModalUploadData, header: string) {
    const ref = this.dialogService.open(ModalUploadComponent, {
      contentStyle: { overflow: 'auto' },
      dismissableMask: true,
      modal: true,
      keepInViewport: true,
      header,
      width: '80vw',
      data: {
        selectedEntity,
        entityType,
      } as ModalUploadData,
    });
  }

  private get entityType() {
    switch (this.type) {
      case 'agreement':
        return 'commessa';
      case 'asset_management':
        return 'asset management';
      case 'ddt':
        return 'DDT';
      case 'delivery':
        return 'consegne';
      case 'order':
        return 'ordini fornitori';
      case 'order_customer':
        return 'ordini clienti';
      case 'schedule':
        return 'schedulazioni';
      case 'ticket':
        return 'ticket';
      case 'warehouse':
        return 'movimento magazzino';
      default:
        return "l'entità selezionata";
    }
  }
}

export abstract class AdvancedListUploadComponent<T> extends AdvancedListComponent<T> {
  private localCommon = inject(LocalCommonService);
  protected dialogService = inject(DialogService);
  protected allegatiService = inject(AllegatiService);
  protected toastService = inject(MessageService);
  abstract type: AttachmentType;
  protected allAttachments: AttachmentsItem[] = [];
  protected attachments: AttachmentsItem[] = [];
  protected selectedEntity?: AttachmentEntity;
  protected exportItems: MenuItem[] = [];
  protected tbl_attachments: MenuItem[] = [];

  protected selectedItems: T[] = [];

  async loadAttachments() {
    this.localCommon.attachments.subscribe({
      next: attachments => {
        this.allAttachments = attachments;
        this.attachments = attachments.filter(a => {
          switch (this.type) {
            case 'agreement':
              return a.for_agreement;
            case 'asset_management':
              return a.for_asset_management;
            case 'ddt':
              return a.for_ddt;
            case 'delivery':
              return a.for_delivery;
            case 'order':
              return a.for_order;
            case 'order_customer':
              return a.for_order_customer;
            case 'schedule':
              return a.for_schedule;
            case 'ticket':
              return a.for_ticket;
            case 'warehouse':
              return a.for_warehouse;
            default:
              return false;
          }
        });
        this.exportItems = [
          {
            icon: 'pi pi-download',
            label: 'Esporta',
            items: this.allAttachments.map(a => ({
              label: `Esporta ${a.type_description}`,
              command: () => {
                this.exportAllDownload(a);
              },
            })),
          },
        ];
      },
    });
  }

  protected openUploadActions(
    event: Event,
    entity: AttachmentEntity,
    header: string = `Allegati per ${this.entityType}`
  ) {
    event.stopPropagation();

    this.openUploadModal(
      {
        selectedEntity: entity,
        entityType: this.type,
      },
      header
    );
  }

  protected openUploadModal({ selectedEntity, entityType }: ModalUploadData, header: string) {
    const ref = this.dialogService.open(ModalUploadComponent, {
      contentStyle: { overflow: 'auto' },
      dismissableMask: true,
      modal: true,
      keepInViewport: true,
      header,
      width: '80vw',
      data: {
        selectedEntity,
        entityType,
      } as ModalUploadData,
    });
  }

  private get entityType() {
    switch (this.type) {
      case 'agreement':
        return 'commessa';
      case 'asset_management':
        return 'asset management';
      case 'ddt':
        return 'DDT';
      case 'delivery':
        return 'consegne';
      case 'order':
        return 'ordini fornitori';
      case 'order_customer':
        return 'ordini clienti';
      case 'schedule':
        return 'schedulazioni';
      case 'ticket':
        return 'ticket';
      case 'warehouse':
        return 'movimento magazzino';
      default:
        return "l'entità selezionata";
    }
  }

  private exportAllDownload(a: AttachmentsItem) {
    this.allegatiService
      .attachmentsControllerDownloadAllAttachments$Response({
        entity: this.type as 'ddt' | 'delivery' | 'order' | 'order_customer' | 'ticket',
        entity_media_type: a.id,
        ids: this.selectedItems.map(e => (e as any).id as number),
      })
      .subscribe({
        next: value => {
          downloadFile(value.body, fileNameFromHeaders(value.headers, 'allegati.zip'));
        },
        error: error => {
          console.error(error);
          this.toastService.add({
            severity: 'error',
            summary: 'Errore',
            detail: `Impossibile esportare gli allegati.`,
          });
        },
      });
  }
}
